import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { TableSkeleton } from '../../Components/common/Skeleton';

const PayoutList = () => {
  const { user } = useAuth();
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });

  useEffect(() => {
    fetchPayouts();
  }, [pagination.page, filters]);

  const fetchPayouts = async () => {
    try {
      if (payouts.length > 0) {
        setIsFetching(true);
      }
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...(filters.status && { status: filters.status }),
        ...(filters.search && { search: filters.search })
      });

      const response = await api.get(`/payouts?${params}`);
      setPayouts(response.data?.data?.payouts || []);
      setPagination((prev) => ({
        ...prev,
        total: response.data?.data?.pagination?.total || 0,
        pages: response.data?.data?.pagination?.pages || 0
      }));
    } catch (error) {
      console.error('Failed to load payouts:', error);
      toast.error(error.response?.data?.message || error.response?.data?.error || 'Failed to load payouts');
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPayoutTypeLabel = (payout) => {
    if (payout.payoutType === 'appointment') {
      return {
        label: 'Appointment',
        sublabel: payout.appointment?.bookingId ? `#${payout.appointment.bookingId}` : 'Consultation',
        color: 'bg-indigo-100 text-indigo-700'
      };
    }

    if (payout.payoutType === 'both') {
      return {
        label: 'Surgery',
        sublabel: 'Doctor + Hospital',
        color: 'bg-purple-100 text-purple-700'
      };
    }

    if (payout.payoutType === 'doctor') {
      return {
        label: 'Surgery',
        sublabel: payout.doctor?.user ? `Dr. ${payout.doctor.user.firstName} ${payout.doctor.user.lastName}` : 'Doctor',
        color: 'bg-blue-100 text-blue-700'
      };
    }

    if (payout.payoutType === 'hospital') {
      return {
        label: 'Surgery',
        sublabel: payout.hospital?.hospitalName || 'Hospital',
        color: 'bg-emerald-100 text-emerald-700'
      };
    }

    return {
      label: payout.payoutType?.replace(/_/g, ' ') || '—',
      sublabel: '',
      color: 'bg-gray-100 text-gray-700'
    };
  };

  const getPayoutIdentifier = (payout) => {
    if (payout.payoutType === 'appointment') {
      return payout.appointment?.bookingId || payout.appointment?._id?.slice(-8).toUpperCase() || 'APT-UNKNOWN';
    }
    if (payout.surgery?.bookingId) {
      return payout.surgery.bookingId;
    }
    if (payout.lead?._id) {
      return payout.lead._id.slice(-8).toUpperCase();
    }
    return payout._id?.slice(-8).toUpperCase() || '—';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="h-8 w-32 bg-slate-200 animate-pulse rounded-lg"></div>
        </div>
        <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 p-8">
          <TableSkeleton rows={8} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeIn p-2 sm:p-0" style={{ minHeight: '80vh' }}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-[#143E78] tracking-tight">Earnings & Payouts</h1>
          <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mt-1">Financial Settlement History</p>
        </div>
      </div>

      {/* Modern Filter Suite */}
      <div className="bg-white rounded-[32px] shadow-xl shadow-gray-200/50 border border-gray-100 p-6 sm:p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="space-y-2">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest pl-1">Search Records</label>
            <div className="relative group">
              <input
                type="text"
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                placeholder="Search by patient name..."
                className="w-full pl-4 pr-4 py-3 bg-gray-50 border border-gray-100 rounded-2xl text-sm font-bold text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
              />
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest pl-1">Settlement Status</label>
            <select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-2xl text-sm font-bold text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all appearance-none"
            >
              <option value="">All Transactions</option>
              <option value="pending">Pending Settlement</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed/Rejected</option>
            </select>
          </div>
        </div>
      </div>

      {/* Payouts Content */}
      <div 
        className={`bg-white rounded-lg shadow overflow-hidden transition-opacity duration-300 ${isFetching ? 'opacity-40 pointer-events-none' : 'opacity-100'}`}
      >
        {/* Mobile View: Handy Cards */}
        <div key={pagination.page} className="block lg:hidden divide-y divide-gray-100 animate-fadeIn">
          {payouts.length > 0 ? (
            payouts.map((payout) => (
              <div key={payout._id} className="p-4 space-y-3">
                 <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <div className="text-sm font-medium text-gray-900">{payout.lead?.patientName || 'Unknown Patient'}</div>
                    <div className="text-[10px] text-gray-400 font-medium uppercase">{getPayoutIdentifier(payout)}</div>
                  </div>
                  <div className="text-right">
                     <div className="text-sm font-bold text-gray-900">₹{(payout.payoutType === 'appointment' ? (payout.doctor?.financials?.consultationFee || payout.amount || 0) : (payout.amount || 0)).toLocaleString('en-IN')}</div>
                    <span className={`inline-block px-2 py-0.5 mt-1 text-[10px] rounded-full ${getStatusColor(payout.status)}`}>
                      {payout.status}
                    </span>
                  </div>
                </div>

                <div className="flex justify-between items-center pt-2 border-t border-gray-50">
                  <div className="text-xs text-gray-500 capitalize flex flex-col">
                    {(() => {
                      const typeInfo = getPayoutTypeLabel(payout);
                      return (
                        <>
                          <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider w-fit ${typeInfo.color}`}>
                            {typeInfo.label}
                          </span>
                          <span className="text-[10px] font-bold text-blue-500 uppercase mt-0.5">
                            {typeInfo.sublabel}
                          </span>
                        </>
                      );
                    })()}
                  </div>
                  <div className="text-[10px] text-gray-400 font-medium">
                    {payout.createdAt ? new Date(payout.createdAt).toLocaleDateString() : 'N/A'}
                  </div>
                </div>

                {payout.documents?.length > 0 && (
                  <a
                    href={payout.documents[0].url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full text-center py-2 bg-blue-50 text-blue-600 text-[10px] font-black uppercase rounded-xl border border-blue-100"
                  >
                    📄 Download Settlement Document
                  </a>
                )}

                <Link
                  to={`/payouts/${payout._id}`}
                  className="block w-full text-center py-2 bg-blue-600 text-white text-xs font-black uppercase tracking-widest rounded-xl shadow-lg shadow-blue-100 active:scale-[0.98] transition-all"
                >
                  View Settlement Details
                </Link>
              </div>
            ))
          ) : (
            <div className="p-10 text-center text-gray-400 italic text-sm">
              No payouts found
            </div>
          )}
        </div>

        {/* Desktop View: Original Table */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gray-50/50 border-b border-gray-100">
                <th className="px-8 py-5 text-left text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Patient / ID</th>
                <th className="px-8 py-5 text-left text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Amount</th>
                <th className="px-8 py-5 text-left text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Payout Type</th>
                <th className="px-8 py-5 text-left text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Status</th>
                <th className="px-8 py-5 text-left text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Settled On</th>
                <th className="px-8 py-5 text-center text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Action</th>
              </tr>
            </thead>
            <tbody key={pagination.page} className="divide-y divide-gray-50 animate-fadeIn">
              {payouts.length > 0 ? (
                payouts.map((payout) => (
                  <tr key={payout._id} className="group hover:bg-blue-50/30 transition-all duration-300">
                    <td className="px-8 py-6 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600 font-black text-xs">
                          {payout.lead?.patientName?.charAt(0) || 'P'}
                        </div>
                      <div>
                        <p className="text-sm font-black text-gray-800">{payout.lead?.patientName || 'Unknown Patient'}</p>
                        <p className="text-[10px] font-bold text-gray-400 tracking-tighter uppercase">{getPayoutIdentifier(payout)}</p>
                      </div>
                      </div>
                    </td>
                    <td className="px-8 py-6 whitespace-nowrap">
                      <p className="text-lg font-black text-[#143E78] tracking-tighter italic">₹{payout.amount?.toLocaleString('en-IN')}</p>
                    </td>
                    <td className="px-8 py-6 whitespace-nowrap">
                      {(() => {
                        const typeInfo = getPayoutTypeLabel(payout);
                        return (
                          <div className="flex flex-col gap-1">
                            <span className={`text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full w-fit ${typeInfo.color}`}>
                              {typeInfo.label}
                            </span>
                            <p className="text-[10px] font-bold text-blue-600 mt-1 uppercase">
                              {typeInfo.sublabel}
                            </p>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-8 py-6 whitespace-nowrap">
                      <span className={`px-4 py-1.5 text-[9px] font-black rounded-full uppercase tracking-widest ${getStatusColor(payout.status)}`}>
                        {payout.status}
                      </span>
                    </td>
                    <td className="px-8 py-6 whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                        <div className="text-[11px] font-bold text-gray-500 uppercase">
                          {payout.createdAt ? new Date(payout.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
                        </div>
                        {payout.documents?.length > 0 && (
                          <a
                            href={payout.documents[0].url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[9px] font-black text-blue-600 uppercase flex items-center gap-1 hover:underline"
                          >
                            📄 Download Invoice
                          </a>
                        )}
                      </div>
                    </td>
                    <td className="px-8 py-6 whitespace-nowrap text-center">
                      <Link
                        to={`/payouts/${payout._id}`}
                        className="px-6 py-2 bg-white border border-gray-100 text-blue-600 text-[10px] font-black uppercase tracking-widest rounded-xl shadow-sm hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-all"
                      >
                        Details
                      </Link>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-8 py-20 text-center">
                    <div className="max-w-xs mx-auto space-y-3">
                      <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto">
                        <p className="text-2xl opacity-20">💰</p>
                      </div>
                      <p className="text-sm font-black text-gray-400 uppercase tracking-widest">No transaction records found</p>
                      <p className="text-[10px] font-medium text-gray-300 leading-relaxed">Your settlement history will appear here once the first deal is finalized.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* High-Fidelity Pagination */}
      {pagination.pages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-6 py-4 px-2">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
            Displaying {pagination.total > 0 ? (pagination.page - 1) * pagination.limit + 1 : 0}-{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} records
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="p-2 border border-gray-100 bg-white rounded-xl disabled:opacity-30 disabled:cursor-not-allowed hover:border-blue-500 hover:text-blue-500 transition-all shadow-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" /></svg>
            </button>
            {Array.from({ length: pagination.pages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`w-9 h-9 text-[11px] font-black rounded-xl transition-all shadow-sm ${pagination.page === page
                  ? 'bg-blue-600 text-white shadow-blue-900/20 scale-110'
                  : 'bg-white border border-gray-100 text-gray-400 hover:border-blue-500 hover:text-blue-500'
                  }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.pages}
              className="p-2 border border-gray-100 bg-white rounded-xl disabled:opacity-30 disabled:cursor-not-allowed hover:border-blue-500 hover:text-blue-500 transition-all shadow-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" /></svg>
            </button>
          </div>
        </div>
      )}
    </div>

  );
};

export default PayoutList;
